import { Link } from 'react-router-dom';
import { MessageSquare, Phone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import WhatsAppMessagePreview from '@/components/WhatsAppMessagePreview';
import { normalize, allPhotoOptions, allVideoOptions, selectedText } from '@/lib/generatedContent';
import type { PreviewTemplate, SendRequirements } from '@/lib/whatsappPreview';

// The message a launched campaign actually sent, as the recipient saw it.
//
// This replaces a card that listed the whole creative — the copy in a textarea, every selected
// photo, every selected video. That answered "what is in this media item", which is a question the
// media library already answers, and never answered the one somebody opening a sent campaign is
// actually asking: what did these five people receive? The photos in particular were actively
// misleading on an SMS campaign, because SMS delivers none of them.
//
// So: one bubble showing the delivered message, and a LINK to the creative for anyone who wants the
// assets. The rendering is `WhatsAppMessagePreview`, the same component the draft editor previews
// with — a launched campaign and a draft must not disagree about how a message looks.

export interface SentMedia {
  id: string;
  name: string;
  deletedAt?: string | null;
  generatedContent?: unknown;
}

export interface SentCampaign {
  type?: string;
  launchConfig?: {
    channel?: string;
    message?: string;
  } | null;
  metadata?: {
    /** The body as it actually went out, recorded at launch. Absent on pre-existing campaigns. */
    sentMessage?: string | null;
    scheduledMessage?: string | null;
    channel?: string | null;
    headerText?: string | null;
    buttonValues?: Record<string, string> | null;
    headerMedia?: { assetUid?: string | null; url?: string | null; resolvedMediaId?: string | null } | null;
  } | null;
  whatsappTemplate?: (PreviewTemplate & { sendRequirements?: SendRequirements | null }) | null;
  media?: SentMedia | null;
}

interface CampaignSentMessageProps {
  campaign: SentCampaign;
  workspaceId: string;
}

/**
 * The image or video that filled a WhatsApp media header, looked up in the creative by uid.
 *
 * `metadata.headerMedia` deliberately stores a re-resolvable SOURCE rather than the provider media
 * id (those expire after 30 days), so the asset is found the same way a relaunch would find it. A
 * one-off upload has neither a uid nor a URL — WhatsApp holds that file by id and there is nothing
 * to render, which the preview shows as a labelled placeholder rather than a broken image.
 */
function resolveHeaderMedia(campaign: SentCampaign) {
  const source = campaign.metadata?.headerMedia;
  if (!source) return null;

  if (source.assetUid && campaign.media) {
    const gc = normalize(campaign.media.generatedContent);
    const photo = allPhotoOptions(gc).find((o) => o.uid === source.assetUid);
    if (photo) return { kind: 'photo' as const, previewUrl: photo.url ?? null };

    const video = allVideoOptions(gc).find((o) => o.uid === source.assetUid);
    if (video) return { kind: 'video' as const, previewUrl: video.url ?? null };
  }

  if (source.url) return { kind: 'photo' as const, previewUrl: source.url };

  return { kind: 'photo' as const, previewUrl: null, note: 'Uploaded directly to WhatsApp' };
}

/**
 * The SMS body this campaign sent.
 *
 * Prefers what was recorded at launch. The fallback reproduces the launch's own expression for
 * campaigns sent before that recording existed — correct at the time, but only a reconstruction:
 * it reads the creative's CURRENT selected text, which somebody may have changed since.
 */
function resolveSmsBody(campaign: SentCampaign): string {
  const recorded = campaign.metadata?.sentMessage ?? campaign.metadata?.scheduledMessage;
  if (recorded) return recorded;

  const custom = campaign.launchConfig?.message;
  if (custom?.trim()) return custom;

  return selectedText(normalize(campaign.media?.generatedContent))?.content || '';
}

export default function CampaignSentMessage({ campaign, workspaceId }: CampaignSentMessageProps) {
  // `metadata.channel` is written at launch and is the authority — the channel is chosen per launch,
  // while `campaign.type` only says what the campaign was set up as. A template is decisive on its
  // own, since only WhatsApp sends have one.
  const channel = campaign.whatsappTemplate
    ? 'whatsapp'
    : campaign.metadata?.channel || campaign.launchConfig?.channel || campaign.type || 'sms';
  const isWhatsApp = channel === 'whatsapp';

  const smsBody = isWhatsApp ? null : resolveSmsBody(campaign);
  const media = campaign.media;
  // Included with `paranoid: false` so the record survives, but there is no page left to link to.
  const mediaDeleted = Boolean(media?.deletedAt);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {isWhatsApp
          ? <MessageSquare className="h-4 w-4 text-green-500" />
          : <Phone className="h-4 w-4 text-muted-foreground" />}
        <span className="text-sm font-medium">{isWhatsApp ? 'WhatsApp' : 'SMS'}</span>
        {isWhatsApp && campaign.whatsappTemplate?.name && (
          <Badge variant="outline" className="text-xs font-normal">
            {campaign.whatsappTemplate.name}
          </Badge>
        )}
      </div>

      {isWhatsApp && !campaign.whatsappTemplate ? (
        // The template was hard-deleted, so what was sent genuinely cannot be reproduced. Saying so
        // is better than rendering an empty bubble that reads as "we sent nothing".
        <p className="text-sm italic text-muted-foreground">
          The template this campaign sent is no longer available, so the message cannot be shown.
        </p>
      ) : !isWhatsApp && !smsBody ? (
        <p className="text-sm italic text-muted-foreground">
          No message body was recorded for this send.
        </p>
      ) : (
        <WhatsAppMessagePreview
          template={isWhatsApp ? campaign.whatsappTemplate : null}
          requirements={isWhatsApp ? campaign.whatsappTemplate?.sendRequirements : null}
          headerMedia={isWhatsApp ? resolveHeaderMedia(campaign) : null}
          headerText={campaign.metadata?.headerText || undefined}
          buttonValues={campaign.metadata?.buttonValues}
          smsBody={smsBody}
          // No recipient: a launched campaign sent one message per person, so resolving the
          // variables against any single contact would show one recipient's copy as if it were
          // everybody's. The per-recipient outcomes are in the table below instead.
          recipient={null}
        />
      )}

      {media && (
        <p className="text-xs text-muted-foreground">
          Creative:{' '}
          {mediaDeleted ? (
            <span className="italic">{media.name} (deleted)</span>
          ) : (
            <Link
              to={`/w/${workspaceId}/campaigns/media/${media.id}`}
              className="underline decoration-dotted hover:text-foreground"
            >
              {media.name}
            </Link>
          )}
          {isWhatsApp
            ? ' — the header media above came from it. Its other photos and videos are stored there.'
            : ' — its photos and videos are stored there and are not delivered by SMS.'}
        </p>
      )}
    </div>
  );
}
